import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { LogOut, Inbox, Layers, Boxes, Trash2, Plus, Pencil, Check } from "lucide-react";

const TABS = [
  { id: "inquiries", label: "Inquiries", icon: Inbox },
  { id: "projects", label: "Projects", icon: Layers },
  { id: "products", label: "Products", icon: Boxes },
];

const emptyProject = { title: "", category: "Residential", location: "", description: "", image_url: "" };
const emptyProduct = { name: "", category: "Kitchens", description: "", image_url: "", features: [] };

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("inquiries");
  const [user, setUser] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [products, setProducts] = useState([]);

  const [projModal, setProjModal] = useState(null);
  const [prodModal, setProdModal] = useState(null);

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => nav("/admin/login"));
  }, [nav]);

  useEffect(() => {
    if (!user) return;
    refresh();
  }, [user, tab]);

  const refresh = async () => {
    try {
      if (tab === "inquiries") {
        const { data } = await api.get("/admin/inquiries");
        setInquiries(data);
      } else if (tab === "projects") {
        const { data } = await api.get("/projects");
        setProjects(data);
      } else {
        const { data } = await api.get("/products");
        setProducts(data);
      }
    } catch (e) {
      if (e.response?.status === 401) nav("/admin/login");
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("bm_token");
    localStorage.removeItem("bm_user");
    nav("/admin/login");
  };

  const updateStatus = async (id, status) => {
    try { await api.put(`/admin/inquiries/${id}`, { status }); refresh(); toast.success("Updated"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delInquiry = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    try { await api.delete(`/admin/inquiries/${id}`); refresh(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveProject = async () => {
    try {
      if (projModal.id) await api.put(`/admin/projects/${projModal.id}`, projModal);
      else await api.post("/admin/projects", projModal);
      setProjModal(null); refresh(); toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delProject = async (id) => {
    if (!window.confirm("Delete project?")) return;
    try { await api.delete(`/admin/projects/${id}`); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveProduct = async () => {
    try {
      const payload = { ...prodModal, features: Array.isArray(prodModal.features) ? prodModal.features : String(prodModal.features || "").split(",").map(s=>s.trim()).filter(Boolean) };
      if (prodModal.id) await api.put(`/admin/products/${prodModal.id}`, payload);
      else await api.post("/admin/products", payload);
      setProdModal(null); refresh(); toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delProduct = async (id) => {
    if (!window.confirm("Delete product?")) return;
    try { await api.delete(`/admin/products/${id}`); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!user) return <div className="min-h-screen grid place-items-center bg-[#F9F8F4]">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#F9F8F4] flex">
      {/* Sidebar */}
      <aside className="w-64 bg-[#1A1A1A] text-[#F9F8F4] flex flex-col">
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-[#8C5931] grid place-items-center font-serif">BM</div>
            <div>
              <div className="font-serif text-lg">Bolt Modular</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#C9BFA9]">Admin</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded text-sm transition-colors ${
                tab === t.id ? "bg-[#8C5931] text-white" : "text-[#C9BFA9] hover:bg-white/5"
              }`}
            >
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="text-xs text-[#C9BFA9] mb-3">{user.email}</div>
          <button data-testid="admin-logout" onClick={logout} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded border border-white/15 text-sm hover:bg-white/5">
            <LogOut size={14}/> Logout
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 p-8 md:p-12 overflow-x-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-serif text-3xl md:text-4xl capitalize">{tab}</h1>
          {tab === "projects" && (
            <button data-testid="add-project-btn" onClick={()=>setProjModal({ ...emptyProject })} className="inline-flex items-center gap-2 bg-[#2B2623] text-[#F9F8F4] px-5 py-3 rounded-full text-sm hover:bg-[#8C5931]"><Plus size={16}/>New Project</button>
          )}
          {tab === "products" && (
            <button data-testid="add-product-btn" onClick={()=>setProdModal({ ...emptyProduct, features: "" })} className="inline-flex items-center gap-2 bg-[#2B2623] text-[#F9F8F4] px-5 py-3 rounded-full text-sm hover:bg-[#8C5931]"><Plus size={16}/>New Product</button>
          )}
        </div>

        {tab === "inquiries" && (
          <div className="bg-white border border-[#E5E0D5] rounded-md overflow-hidden">
            <table className="w-full text-sm" data-testid="inquiries-table">
              <thead className="bg-[#F3F1EA] text-left">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Contact</th>
                  <th className="px-4 py-3">Service</th>
                  <th className="px-4 py-3">Message</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">When</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {inquiries.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-10 text-[#6E6862]">No inquiries yet.</td></tr>
                )}
                {inquiries.map((q) => (
                  <tr key={q.id} className="border-t border-[#E5E0D5]">
                    <td className="px-4 py-3 font-medium">{q.name}</td>
                    <td className="px-4 py-3 text-[#6E6862]">{q.email}<br/>{q.phone}</td>
                    <td className="px-4 py-3">{q.service}</td>
                    <td className="px-4 py-3 max-w-xs text-[#6E6862]">{q.message}</td>
                    <td className="px-4 py-3">
                      <select value={q.status} onChange={(e)=>updateStatus(q.id, e.target.value)} className="border border-[#E5E0D5] rounded px-2 py-1 text-xs">
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="closed">Closed</option>
                      </select>
                    </td>
                    <td className="px-4 py-3 text-xs text-[#6E6862]">{new Date(q.created_at).toLocaleString()}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={()=>delInquiry(q.id)} className="text-red-600 hover:opacity-70"><Trash2 size={16}/></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "projects" && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="projects-grid">
            {projects.map((p) => (
              <div key={p.id} className="bg-white border border-[#E5E0D5] rounded-md overflow-hidden">
                <img src={p.image_url} alt={p.title} className="w-full aspect-video object-cover"/>
                <div className="p-5">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#8C5931]">{p.category}</div>
                  <h3 className="font-serif text-xl mt-1">{p.title}</h3>
                  <p className="text-xs text-[#6E6862] mt-2">{p.location}</p>
                  <div className="flex gap-2 mt-4">
                    <button onClick={()=>setProjModal(p)} className="text-xs flex items-center gap-1 text-[#2B2623] hover:text-[#8C5931]"><Pencil size={14}/>Edit</button>
                    <button onClick={()=>delProject(p.id)} className="text-xs flex items-center gap-1 text-red-600 hover:opacity-70"><Trash2 size={14}/>Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "products" && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="products-grid">
            {products.map((p) => (
              <div key={p.id} className="bg-white border border-[#E5E0D5] rounded-md overflow-hidden">
                <img src={p.image_url} alt={p.name} className="w-full aspect-video object-cover"/>
                <div className="p-5">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#8C5931]">{p.category}</div>
                  <h3 className="font-serif text-xl mt-1">{p.name}</h3>
                  <p className="text-xs text-[#6E6862] mt-2 line-clamp-2">{p.description}</p>
                  <div className="flex gap-2 mt-4">
                    <button onClick={()=>setProdModal({ ...p, features: (p.features || []).join(", ") })} className="text-xs flex items-center gap-1 text-[#2B2623] hover:text-[#8C5931]"><Pencil size={14}/>Edit</button>
                    <button onClick={()=>delProduct(p.id)} className="text-xs flex items-center gap-1 text-red-600 hover:opacity-70"><Trash2 size={14}/>Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Project Modal */}
      {projModal && (
        <Modal onClose={()=>setProjModal(null)} title={projModal.id ? "Edit Project" : "New Project"}>
          <Field label="Title" value={projModal.title} onChange={(v)=>setProjModal({...projModal, title:v})} testid="proj-title"/>
          <Field label="Category" value={projModal.category} onChange={(v)=>setProjModal({...projModal, category:v})} type="select" options={["Residential","Commercial","Hospitality"]} testid="proj-category"/>
          <Field label="Location" value={projModal.location} onChange={(v)=>setProjModal({...projModal, location:v})} testid="proj-location"/>
          <Field label="Image URL" value={projModal.image_url} onChange={(v)=>setProjModal({...projModal, image_url:v})} testid="proj-image"/>
          <Field label="Description" value={projModal.description} onChange={(v)=>setProjModal({...projModal, description:v})} type="textarea" testid="proj-desc"/>
          <button data-testid="save-project" onClick={saveProject} className="w-full bg-[#2B2623] text-[#F9F8F4] py-3 rounded-full hover:bg-[#8C5931] flex items-center justify-center gap-2"><Check size={16}/>Save</button>
        </Modal>
      )}

      {/* Product Modal */}
      {prodModal && (
        <Modal onClose={()=>setProdModal(null)} title={prodModal.id ? "Edit Product" : "New Product"}>
          <Field label="Name" value={prodModal.name} onChange={(v)=>setProdModal({...prodModal, name:v})} testid="prod-name"/>
          <Field label="Category" value={prodModal.category} onChange={(v)=>setProdModal({...prodModal, category:v})} type="select" options={["Kitchens","Wardrobes","Doors","Furniture","Materials"]} testid="prod-category"/>
          <Field label="Image URL" value={prodModal.image_url} onChange={(v)=>setProdModal({...prodModal, image_url:v})} testid="prod-image"/>
          <Field label="Features (comma separated)" value={prodModal.features} onChange={(v)=>setProdModal({...prodModal, features:v})} testid="prod-features"/>
          <Field label="Description" value={prodModal.description} onChange={(v)=>setProdModal({...prodModal, description:v})} type="textarea" testid="prod-desc"/>
          <button data-testid="save-product" onClick={saveProduct} className="w-full bg-[#2B2623] text-[#F9F8F4] py-3 rounded-full hover:bg-[#8C5931] flex items-center justify-center gap-2"><Check size={16}/>Save</button>
        </Modal>
      )}
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div onClick={(e)=>e.stopPropagation()} className="bg-white max-w-lg w-full p-8 rounded-md max-h-[90vh] overflow-y-auto">
        <h3 className="font-serif text-2xl mb-6">{title}</h3>
        <div className="space-y-4">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type="text", options=[], testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] text-[#6E6862]">{label}</label>
      {type === "textarea" ? (
        <textarea data-testid={testid} value={value || ""} onChange={(e)=>onChange(e.target.value)} rows={3} className="mt-2 w-full border border-[#E5E0D5] rounded px-3 py-2 outline-none focus:border-[#8C5931]"/>
      ) : type === "select" ? (
        <select data-testid={testid} value={value || ""} onChange={(e)=>onChange(e.target.value)} className="mt-2 w-full border border-[#E5E0D5] rounded px-3 py-2 outline-none focus:border-[#8C5931]">
          {options.map((o)=>(<option key={o} value={o}>{o}</option>))}
        </select>
      ) : (
        <input data-testid={testid} value={value || ""} onChange={(e)=>onChange(e.target.value)} className="mt-2 w-full border border-[#E5E0D5] rounded px-3 py-2 outline-none focus:border-[#8C5931]"/>
      )}
    </div>
  );
}
